import React, { useState, useEffect } from 'react';
import { useAuth, api } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  BookOpen,
  Key,
  Copy,
  CheckCircle,
  Code,
  Send,
  MapPin,
  FileText,
  Image,
  MessageSquare,
  Clock,
  AlertCircle,
  RefreshCw,
  Loader2,
  ExternalLink,
  UserPlus,
  Users,
  Eye,
  EyeOff
} from 'lucide-react';

const API_BASE_URL = window.location.origin;

const CodeBlock = ({ children, language = 'json' }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(children);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div className="relative group">
      <pre className="bg-slate-900 text-slate-100 p-4 rounded-xl overflow-x-auto text-sm font-mono">
        <code>{children}</code>
      </pre>
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 p-2 bg-slate-700 hover:bg-slate-600 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
      >
        {copied ? (
          <CheckCircle className="w-4 h-4 text-green-400" />
        ) : (
          <Copy className="w-4 h-4 text-slate-300" />
        )}
      </button>
    </div>
  );
};

const EndpointCard = ({ method, path, title, description, requestBody, responseBody, children }) => {
  const methodColors = {
    GET: 'bg-green-100 text-green-700',
    POST: 'bg-blue-100 text-blue-700',
    PUT: 'bg-amber-100 text-amber-700',
    DELETE: 'bg-red-100 text-red-700'
  };
  
  return (
    <Card className="premium-card border-0 mb-6">
      <CardHeader>
        <div className="flex items-center gap-3 mb-2">
          <Badge className={`${methodColors[method]} font-mono font-bold`}>{method}</Badge>
          <code className="text-sm font-mono text-slate-600">{path}</code>
        </div>
        <CardTitle className="text-lg">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {requestBody && (
          <div>
            <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
              <Send className="w-4 h-4" />
              Request Body
            </h4>
            <CodeBlock>{requestBody}</CodeBlock>
          </div>
        )}
        {responseBody && (
          <div>
            <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Response
            </h4>
            <CodeBlock>{responseBody}</CodeBlock>
          </div>
        )}
        {children}
      </CardContent>
    </Card>
  );
};

const ApiDocumentationPage = () => {
  const { token, hasRole, user } = useAuth();
  const [apiKeys, setApiKeys] = useState([]);
  const [apiUsers, setApiUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUserData, setNewUserData] = useState({ email: '', password: '', name: '' });
  const [creatingUser, setCreatingUser] = useState(false);

  useEffect(() => {
    fetchApiKeys();
    if (hasRole(['mestre'])) {
      fetchApiUsers();
    }
  }, []);

  const fetchApiKeys = async () => {
    try {
      const keys = await api.get('/api-keys', token);
      setApiKeys(keys);
    } catch (error) {
      console.error('Error fetching API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchApiUsers = async () => {
    try {
      const result = await api.get('/auth/api-users', token);
      setApiUsers(result.users || []);
    } catch (error) {
      console.error('Error fetching API users:', error);
    }
  };

  const generateApiKey = async () => {
    setGenerating(true);
    try {
      const result = await api.post('/generate-api-key', {}, token);
      if (result.api_key) {
        setNewKey(result.api_key);
        toast.success('API Key gerada com sucesso!');
      } else {
        toast.info(result.message);
      }
      fetchApiKeys();
    } catch (error) {
      toast.error('Erro ao gerar API Key');
    } finally {
      setGenerating(false);
    }
  };

  const createApiUser = async () => {
    if (!newUserData.email || !newUserData.password || !newUserData.name) {
      toast.error('Preencha todos os campos');
      return;
    }
    
    setCreatingUser(true);
    try {
      await api.post(`/auth/create-api-user?email=${encodeURIComponent(newUserData.email)}&password=${encodeURIComponent(newUserData.password)}&name=${encodeURIComponent(newUserData.name)}`, {}, token);
      toast.success('Usuário de API criado com sucesso!');
      setShowCreateUser(false);
      setNewUserData({ email: '', password: '', name: '' });
      fetchApiUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar usuário');
    } finally {
      setCreatingUser(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado!');
  };

  // Render the documentation tabs (shared between api_access and mestre)
  const renderDocumentationTabs = () => (
    <Tabs defaultValue="overview" className="space-y-4">
      <TabsList className="bg-white p-1 rounded-xl flex-wrap">
        <TabsTrigger value="overview" className="rounded-lg">Visão Geral</TabsTrigger>
        <TabsTrigger value="missions" className="rounded-lg">Missões</TabsTrigger>
        <TabsTrigger value="customers" className="rounded-lg">Clientes</TabsTrigger>
        <TabsTrigger value="suppliers" className="rounded-lg">Fornecedores</TabsTrigger>
        <TabsTrigger value="carriers" className="rounded-lg">Transportadores</TabsTrigger>
        <TabsTrigger value="updates" className="rounded-lg">Atualizações</TabsTrigger>
        <TabsTrigger value="chat" className="rounded-lg">Conversas</TabsTrigger>
      </TabsList>
      <TabsContent value="overview">
        <Card className="premium-card border-0">
          <CardHeader>
            <CardTitle>Visão Geral da API</CardTitle>
            <CardDescription>
              API para integração de sistemas externos com o PR APP
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 rounded-xl bg-slate-50">
                <h4 className="font-medium text-slate-900 mb-2">Base URL</h4>
                <code className="text-sm font-mono text-indigo-600">{API_BASE_URL}/api</code>
              </div>
              <div className="p-4 rounded-xl bg-slate-50">
                <h4 className="font-medium text-slate-900 mb-2">Autenticação</h4>
                <code className="text-sm font-mono">Header: X-API-Key</code>
              </div>
            </div>

            {/* GET Endpoints */}
            <div className="p-4 rounded-xl bg-green-50 border border-green-200">
              <h4 className="font-medium text-green-900 mb-2">Endpoints GET (Consulta de Dados)</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-100 text-green-700 font-mono text-xs">GET</Badge>
                  <code>/external/missions</code>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-100 text-green-700 font-mono text-xs">GET</Badge>
                  <code>/external/missions/stats</code>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-100 text-green-700 font-mono text-xs">GET</Badge>
                  <code>/external/missions/active</code>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-100 text-green-700 font-mono text-xs">GET</Badge>
                  <code>/external/mission/{'{id}'}</code>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-100 text-green-700 font-mono text-xs">GET</Badge>
                  <code>/external/customers</code>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-100 text-green-700 font-mono text-xs">GET</Badge>
                  <code>/external/customers/{'{id}'}</code>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-100 text-green-700 font-mono text-xs">GET</Badge>
                  <code>/external/suppliers</code>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-100 text-green-700 font-mono text-xs">GET</Badge>
                  <code>/external/suppliers/{'{id}'}</code>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-100 text-green-700 font-mono text-xs">GET</Badge>
                  <code>/external/carriers</code>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-100 text-green-700 font-mono text-xs">GET</Badge>
                  <code>/external/carriers/{'{id}'}</code>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-100 text-green-700 font-mono text-xs">GET</Badge>
                  <code>/external/reasons</code>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-100 text-green-700 font-mono text-xs">GET</Badge>
                  <code>/external/tenants</code>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-100 text-green-700 font-mono text-xs">GET</Badge>
                  <code>/external/users</code>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-100 text-green-700 font-mono text-xs">GET</Badge>
                  <code>/external/dashboard/summary</code>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-100 text-green-700 font-mono text-xs">GET</Badge>
                  <code>/external/billing/pending</code>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-100 text-green-700 font-mono text-xs">GET</Badge>
                  <code>/external/search?q=termo</code>
                </div>
              </div>
            </div>

            {/* POST Endpoints */}
            <div className="p-4 rounded-xl bg-blue-50 border border-blue-200">
              <h4 className="font-medium text-blue-900 mb-2">Endpoints POST (Atualização de Dados)</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                <div className="flex items-center gap-2">
                  <Badge className="bg-blue-100 text-blue-700 font-mono text-xs">POST</Badge>
                  <code>/external/mission/availability</code>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-blue-100 text-blue-700 font-mono text-xs">POST</Badge>
                  <code>/external/mission/arrival</code>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-blue-100 text-blue-700 font-mono text-xs">POST</Badge>
                  <code>/external/mission/report</code>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-blue-100 text-blue-700 font-mono text-xs">POST</Badge>
                  <code>/external/mission/media</code>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-blue-100 text-blue-700 font-mono text-xs">POST</Badge>
                  <code>/external/mission/finalize</code>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-blue-100 text-blue-700 font-mono text-xs">POST</Badge>
                  <code>/external/mission/chat</code>
                </div>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
              <h4 className="font-medium text-amber-900 mb-2">Exemplo com cURL</h4>
              <CodeBlock>{`curl -X GET "${API_BASE_URL}/api/external/missions?status=em_andamento&days=7" \\
  -H "X-API-Key: prapp_admin_xxxxxxxxxxxxxxxx"`}</CodeBlock>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      {/* Missions Tab */}
      <TabsContent value="missions">
        <ScrollArea className="h-[600px]">
          <EndpointCard
            method="GET"
            path="/api/external/missions"
            title="Listar Missões"
            description="Retorna lista de missões com filtros opcionais."
            responseBody={`{
  "total": 150,
  "limit": 100,
  "skip": 0,
  "count": 100,
  "missions": [
    {
      "id": "abc123-def456",
      "code": "PR-20260401-1234",
      "status": "em_andamento",
      "customer_name": "LogExpress",
      "supplier_name": "Auto Socorro SP",
      "vehicle_plate": "ABC-1234",
      "created_at": "2026-04-01T10:00:00"
    }
  ]
}`}
          >
            <div className="mt-4 p-4 rounded-xl bg-slate-50">
              <h4 className="font-medium text-slate-900 mb-2">Query Parameters</h4>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b">
                    <th className="pb-2">Parâmetro</th>
                    <th className="pb-2">Tipo</th>
                    <th className="pb-2">Descrição</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="py-2 font-mono">status</td>
                    <td>string</td>
                    <td>Filtrar por status (criada, aguardando_inicio, em_andamento, etc.)</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 font-mono">customer_id</td>
                    <td>string</td>
                    <td>Filtrar por cliente</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 font-mono">supplier_id</td>
                    <td>string</td>
                    <td>Filtrar por fornecedor</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 font-mono">days</td>
                    <td>int</td>
                    <td>Número de dias (padrão: 30)</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 font-mono">limit</td>
                    <td>int</td>
                    <td>Máximo de resultados (padrão: 100, máx: 500)</td>
                  </tr>
                  <tr>
                    <td className="py-2 font-mono">skip</td>
                    <td>int</td>
                    <td>Offset para paginação</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </EndpointCard>

          <EndpointCard
            method="GET"
            path="/api/external/missions/stats"
            title="Estatísticas de Missões"
            description="Retorna estatísticas resumidas das missões."
            responseBody={`{
  "period_days": 30,
  "total_missions": 150,
  "by_status": {
    "criada": 10,
    "aguardando_inicio": 5,
    "em_andamento": 8,
    "finalizada": 100,
    "cancelada": 27
  },
  "completed_missions": 100,
  "average_time_minutes": 45.5,
  "total_km": 5000
}`}
          />

          <EndpointCard
            method="GET"
            path="/api/external/missions/active"
            title="Missões Ativas"
            description="Retorna apenas missões em andamento (criada, aguardando_inicio, em_andamento)."
            responseBody={`{
  "total_active": 23,
  "missions": [...]
}`}
          />

          <EndpointCard
            method="GET"
            path="/api/external/mission/{mission_id}"
            title="Detalhes Completos da Missão"
            description="Retorna todos os dados da missão incluindo chat, evidências e respostas."
            responseBody={`{
  "mission": {
    "id": "abc123",
    "code": "PR-20260401-1234",
    "status": "em_andamento",
    ...
  },
  "chat_history": [...],
  "evidence": [...],
  "supplier_responses": [...]
}`}
          />
        </ScrollArea>
      </TabsContent>

      {/* Customers Tab */}
      <TabsContent value="customers">
        <ScrollArea className="h-[600px]">
          <EndpointCard
            method="GET"
            path="/api/external/customers"
            title="Listar Clientes"
            description="Retorna lista de clientes cadastrados."
            responseBody={`{
  "total": 50,
  "count": 50,
  "customers": [
    {
      "id": "cust001-abc",
      "name": "LogExpress Transportes",
      "cnpj": "12.345.678/0001-90",
      "phone": "(11) 3456-7890",
      "city": "São Paulo",
      "state": "SP",
      "active": true
    }
  ]
}`}
          >
            <div className="mt-4 p-4 rounded-xl bg-slate-50">
              <h4 className="font-medium text-slate-900 mb-2">Query Parameters</h4>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b">
                    <th className="pb-2">Parâmetro</th>
                    <th className="pb-2">Tipo</th>
                    <th className="pb-2">Descrição</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="py-2 font-mono">active</td>
                    <td>boolean</td>
                    <td>Filtrar por status ativo</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 font-mono">tenant_cnpj</td>
                    <td>string</td>
                    <td>Filtrar por organização</td>
                  </tr>
                  <tr>
                    <td className="py-2 font-mono">search</td>
                    <td>string</td>
                    <td>Buscar por nome ou CNPJ</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </EndpointCard>

          <EndpointCard
            method="GET"
            path="/api/external/customers/{customer_id}"
            title="Detalhes do Cliente"
            description="Retorna dados completos de um cliente específico."
            responseBody={`{
  "customer": {
    "id": "cust001-abc",
    "name": "LogExpress Transportes",
    "cnpj": "12.345.678/0001-90",
    "pricing": [...]
  },
  "stats": {
    "total_missions": 150,
    "active_missions": 3
  }
}`}
          />
        </ScrollArea>
      </TabsContent>

      {/* Suppliers Tab */}
      <TabsContent value="suppliers">
        <ScrollArea className="h-[600px]">
          <EndpointCard
            method="GET"
            path="/api/external/suppliers"
            title="Listar Fornecedores"
            description="Retorna lista de fornecedores cadastrados."
            responseBody={`{
  "total": 30,
  "count": 30,
  "suppliers": [
    {
      "id": "sup001-xyz",
      "name": "Auto Socorro SP",
      "phone": "+5511989213046",
      "service_types": ["guincho", "mecanico"],
      "active": true
    }
  ]
}`}
          >
            <div className="mt-4 p-4 rounded-xl bg-slate-50">
              <h4 className="font-medium text-slate-900 mb-2">Query Parameters</h4>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b">
                    <th className="pb-2">Parâmetro</th>
                    <th className="pb-2">Tipo</th>
                    <th className="pb-2">Descrição</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="py-2 font-mono">active</td>
                    <td>boolean</td>
                    <td>Filtrar por status ativo</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 font-mono">service_type</td>
                    <td>string</td>
                    <td>Filtrar por tipo de serviço</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 font-mono">city</td>
                    <td>string</td>
                    <td>Filtrar por cidade</td>
                  </tr>
                  <tr>
                    <td className="py-2 font-mono">search</td>
                    <td>string</td>
                    <td>Buscar por nome, telefone ou CNPJ</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </EndpointCard>

          <EndpointCard
            method="GET"
            path="/api/external/suppliers/{supplier_id}"
            title="Detalhes do Fornecedor"
            description="Retorna dados completos de um fornecedor específico."
            responseBody={`{
  "supplier": {
    "id": "sup001-xyz",
    "name": "Auto Socorro SP",
    "phone": "+5511989213046",
    "service_types": ["guincho", "mecanico"]
  },
  "stats": {
    "total_missions": 250,
    "completed_missions": 230,
    "active_missions": 2
  }
}`}
          />

          <EndpointCard
            method="GET"
            path="/api/external/suppliers/{supplier_id}/missions"
            title="Missões do Fornecedor"
            description="Lista missões atribuídas a um fornecedor específico."
            responseBody={`{
  "supplier_id": "sup001-xyz",
  "count": 25,
  "missions": [...]
}`}
          />
        </ScrollArea>
      </TabsContent>

      {/* Carriers Tab */}
      <TabsContent value="carriers">
        <ScrollArea className="h-[600px]">
          <EndpointCard
            method="GET"
            path="/api/external/carriers"
            title="Listar Transportadores"
            description="Retorna lista de transportadores cadastrados."
            responseBody={`{
  "total": 20,
  "count": 20,
  "carriers": [
    {
      "id": "car001-abc",
      "name": "TransLog Express",
      "cnpj": "11.222.333/0001-44",
      "fleet_size": 50,
      "active": true
    }
  ]
}`}
          />

          <EndpointCard
            method="GET"
            path="/api/external/carriers/{carrier_id}"
            title="Detalhes do Transportador"
            description="Retorna dados completos de um transportador específico."
            responseBody={`{
  "carrier": {
    "id": "car001-abc",
    "name": "TransLog Express",
    "fleet_size": 50
  },
  "stats": {
    "total_missions": 500
  }
}`}
          />

          <EndpointCard
            method="GET"
            path="/api/external/reasons"
            title="Listar Motivos de Missão"
            description="Retorna lista de motivos de missão cadastrados."
            responseBody={`{
  "total": 9,
  "reasons": [
    {
      "id": "rea001",
      "code": "PM",
      "name": "Pane Mecânica",
      "category": "mecanica",
      "active": true
    }
  ]
}`}
          />

          <EndpointCard
            method="GET"
            path="/api/external/tenants"
            title="Listar Organizações"
            description="Retorna lista de organizações/tenants."
            responseBody={`{
  "total": 2,
  "tenants": [
    {
      "id": "ten001",
      "cnpj": "12.345.678/0001-90",
      "company_name": "Concectiva Tecnologia",
      "approved": true
    }
  ]
}`}
          />
        </ScrollArea>
      </TabsContent>

      {/* Updates Tab (POST endpoints) */}
      <TabsContent value="updates">
        <ScrollArea className="h-[600px]">
          <EndpointCard
            method="POST"
            path="/api/external/mission/availability"
            title="Atualizar Disponibilidade"
            description="Atualiza a disponibilidade de um fornecedor para uma missão."
            requestBody={`{
  "mission_id": "abc123-def456",
  "supplier_id": "sup001-xyz",
  "available": true,
  "eta_datetime": "2026-04-01T15:30:00",
  "notes": "A caminho"
}`}
            responseBody={`{
  "status": "success",
  "message": "Availability updated"
}`}
          />

          <EndpointCard
            method="POST"
            path="/api/external/mission/arrival"
            title="Registrar Chegada"
            description="Registra a chegada do fornecedor na origem."
            requestBody={`{
  "mission_id": "abc123-def456",
  "supplier_id": "sup001-xyz",
  "arrived_at": "2026-04-01T15:45:00",
  "odometer_start": 145230,
  "notes": "Cheguei no local"
}`}
            responseBody={`{
  "status": "success",
  "arrived_at": "2026-04-01T15:45:00",
  "mission_status": "em_andamento"
}`}
          />

          <EndpointCard
            method="POST"
            path="/api/external/mission/report"
            title="Enviar Relato"
            description="Adiciona um relato de progresso à missão."
            requestBody={`{
  "mission_id": "abc123-def456",
  "supplier_id": "sup001-xyz",
  "report_text": "Iniciando troca do pneu",
  "report_type": "progress"
}`}
            responseBody={`{
  "status": "success",
  "report_type": "progress"
}`}
          />

          <EndpointCard
            method="POST"
            path="/api/external/mission/media"
            title="Anexar Mídia"
            description="Anexa foto, vídeo ou documento à missão."
            requestBody={`{
  "mission_id": "abc123-def456",
  "supplier_id": "sup001-xyz",
  "media_url": "https://storage.example.com/photo.jpg",
  "media_type": "photo",
  "description": "Foto do hodômetro"
}`}
            responseBody={`{
  "status": "success",
  "evidence_id": "ev001-abc"
}`}
          />

          <EndpointCard
            method="POST"
            path="/api/external/mission/finalize"
            title="Finalizar Missão"
            description="Marca a missão como finalizada."
            requestBody={`{
  "mission_id": "abc123-def456",
  "supplier_id": "sup001-xyz",
  "finished_at": "2026-04-01T17:30:00",
  "odometer_end": 145280,
  "final_notes": "Atendimento concluído"
}`}
            responseBody={`{
  "status": "success",
  "total_km": 50,
  "total_time_minutes": 105,
  "mission_status": "aguardando_validacao"
}`}
          />
        </ScrollArea>
      </TabsContent>

      {/* Chat Tab */}
      <TabsContent value="chat">
        <ScrollArea className="h-[600px]">
          <EndpointCard
            method="POST"
            path="/api/external/mission/chat"
            title="Enviar Mensagem"
            description="Envia uma mensagem no chat da missão."
            requestBody={`{
  "mission_id": "abc123-def456",
  "sender_type": "supplier",
  "sender_id": "sup001-xyz",
  "sender_name": "Auto Socorro SP",
  "content": "Iniciando atendimento"
}`}
            responseBody={`{
  "status": "success",
  "message_id": "msg001-abc",
  "created_at": "2026-04-01T16:00:00"
}`}
          />

          <EndpointCard
            method="GET"
            path="/api/external/mission/{mission_id}/chat"
            title="Obter Conversa Completa"
            description="Retorna todas as mensagens da conversa de uma missão."
            responseBody={`{
  "mission_id": "abc123",
  "mission_code": "PR-20260401-1234",
  "total_messages": 15,
  "messages": [
    {
      "id": "msg001",
      "sender_type": "platform",
      "sender_name": "Sistema",
      "content": "🚨 Nova missão criada",
      "created_at": "2026-04-01T10:00:00"
    },
    {
      "id": "msg002",
      "sender_type": "supplier",
      "sender_name": "Auto Socorro SP",
      "content": "✅ DISPONÍVEL - ETA: 15:30",
      "created_at": "2026-04-01T10:15:00"
    }
  ]
}`}
          />

          <EndpointCard
            method="GET"
            path="/api/external/mission/{mission_id}/evidence"
            title="Obter Evidências"
            description="Retorna todas as evidências/anexos de uma missão."
            responseBody={`{
  "mission_id": "abc123",
  "total": 5,
  "evidence": [
    {
      "id": "ev001",
      "type": "photo",
      "url": "https://...",
      "description": "Foto do hodômetro inicial",
      "created_at": "2026-04-01T15:45:00"
    }
  ]
}`}
          />
        </ScrollArea>
      </TabsContent>
    </Tabs>
  );

  // API Access users see simplified view (without user management)
  if (user?.role === 'api_access') {
    return (
      <div className="space-y-6" data-testid="api-documentation-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
            <BookOpen className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Public Sans' }}>
              Documentação da API Externa
            </h1>
            <p className="text-slate-500">Integração via API para atualização de dados de missões</p>
          </div>
        </div>

        {/* API Key Info */}
        <Card className="premium-card border-0 bg-gradient-to-br from-indigo-50 to-purple-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-indigo-600" />
              Autenticação
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="p-4 rounded-xl bg-slate-100">
              <h4 className="font-medium text-slate-900 mb-2">Como Autenticar:</h4>
              <p className="text-sm text-slate-600 mb-2">
                Solicite uma API Key ao administrador e inclua no header de todas as requisições:
              </p>
              <CodeBlock>{`X-API-Key: prapp_admin_xxxxxxxxxxxxxxxx`}</CodeBlock>
            </div>
          </CardContent>
        </Card>

        {/* Documentation Tabs */}
        {renderDocumentationTabs()}
      </div>
    );
  }

  // Master users see full view with user management
  if (!hasRole(['mestre'])) {
    return (
      <div className="p-8">
        <Card className="premium-card border-0">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Acesso Restrito</h2>
            <p className="text-slate-500">Apenas usuários Master podem acessar esta documentação.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 pl-14 pb-12" data-testid="api-documentation-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
          <BookOpen className="w-6 h-6 text-indigo-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Public Sans' }}>
            Documentação da API Externa
          </h1>
          <p className="text-slate-500">Integração via API para atualização de dados de missões</p>
        </div>
      </div>

      {/* API Key Management */}
      <Card className="premium-card border-0 bg-gradient-to-br from-indigo-50 to-purple-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5 text-indigo-600" />
            Chaves de API (Tokens de Autenticação)
          </CardTitle>
          <CardDescription>
            Tokens que não expiram para autenticação nas requisições
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4">
            <Button 
              onClick={generateApiKey}
              disabled={generating}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {generating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Key className="w-4 h-4 mr-2" />
              )}
              Gerar Nova API Key
            </Button>
            <Button 
              variant="outline" 
              onClick={fetchApiKeys}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Atualizar Lista
            </Button>
          </div>

          {newKey && (
            <div className="p-4 rounded-xl bg-green-50 border border-green-200">
              <h4 className="font-medium text-green-900 mb-2 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Nova API Key Gerada
              </h4>
              <p className="text-sm text-green-800 mb-2">
                ⚠️ Copie esta chave agora. Ela não será exibida novamente!
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 p-3 bg-white rounded-lg font-mono text-sm break-all">
                  {newKey}
                </code>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => copyToClipboard(newKey)}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Lista de API Keys */}
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
            </div>
          ) : apiKeys.length > 0 ? (
            <div className="space-y-2">
              <h4 className="font-medium text-slate-700">Chaves Existentes:</h4>
              {apiKeys.map((key) => (
                <div key={key.id} className="p-3 rounded-lg bg-white border flex items-center justify-between">
                  <div>
                    <p className="font-medium">{key.name}</p>
                    <p className="text-sm text-slate-500">{key.description}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <code className="text-sm font-mono text-slate-500">{key.key_preview}</code>
                    <Badge className={key.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                      {key.active ? 'Ativa' : 'Inativa'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-4">
              Nenhuma API Key cadastrada. Clique em "Gerar Nova API Key" para criar.
            </p>
          )}

          {/* Authentication Instructions */}
          <div className="p-4 rounded-xl bg-slate-100">
            <h4 className="font-medium text-slate-900 mb-2">Como Autenticar:</h4>
            <p className="text-sm text-slate-600 mb-2">
              Inclua a API Key no header de todas as requisições:
            </p>
            <CodeBlock>{`X-API-Key: prapp_admin_xxxxxxxxxxxxxxxx`}</CodeBlock>
          </div>
        </CardContent>
      </Card>

      {/* API Users Management */}
      <Card className="premium-card border-0">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-600" />
            Usuários de Acesso API
          </CardTitle>
          <CardDescription>
            Crie usuários com acesso exclusivo à documentação da API
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4">
            <Button 
              onClick={() => setShowCreateUser(!showCreateUser)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              {showCreateUser ? 'Cancelar' : 'Novo Usuário API'}
            </Button>
          </div>

          {/* Form para criar usuário */}
          {showCreateUser && (
            <div className="p-4 rounded-xl bg-purple-50 border border-purple-200 space-y-4">
              <h4 className="font-medium text-purple-900">Criar Novo Usuário de API</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Nome</Label>
                  <Input
                    placeholder="Nome do desenvolvedor"
                    value={newUserData.name}
                    onChange={(e) => setNewUserData({...newUserData, name: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    placeholder="email@empresa.com"
                    value={newUserData.email}
                    onChange={(e) => setNewUserData({...newUserData, email: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Senha</Label>
                  <Input
                    type="password"
                    placeholder="••••••••"
                    value={newUserData.password}
                    onChange={(e) => setNewUserData({...newUserData, password: e.target.value})}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={createApiUser}
                  disabled={creatingUser}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {creatingUser ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <UserPlus className="w-4 h-4 mr-2" />
                  )}
                  Criar Usuário
                </Button>
              </div>
              <p className="text-xs text-purple-700">
                O usuário poderá acessar a documentação via <code className="bg-purple-100 px-1 rounded">/api-login</code>
              </p>
            </div>
          )}

          {/* Lista de usuários API */}
          {apiUsers.length > 0 ? (
            <div className="space-y-2">
              <h4 className="font-medium text-slate-700">Usuários com Acesso API:</h4>
              {apiUsers.map((user) => (
                <div key={user.id} className="p-3 rounded-lg bg-white border flex items-center justify-between">
                  <div>
                    <p className="font-medium">{user.name}</p>
                    <p className="text-sm text-slate-500">{user.email}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className="bg-purple-100 text-purple-700">API Access</Badge>
                    <Badge className={user.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                      {user.active ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-4">
              Nenhum usuário de API cadastrado.
            </p>
          )}

          <div className="p-4 rounded-xl bg-slate-100">
            <h4 className="font-medium text-slate-900 mb-2">URL de Login para Usuários API:</h4>
            <code className="text-sm font-mono text-purple-600">{API_BASE_URL}/login-api</code>
          </div>
        </CardContent>
      </Card>

      {/* API Documentation Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="bg-white p-1 rounded-xl flex-wrap">
          <TabsTrigger value="overview" className="rounded-lg">Visão Geral</TabsTrigger>
          <TabsTrigger value="missions" className="rounded-lg">Missões</TabsTrigger>
          <TabsTrigger value="customers" className="rounded-lg">Clientes</TabsTrigger>
          <TabsTrigger value="suppliers" className="rounded-lg">Fornecedores</TabsTrigger>
          <TabsTrigger value="carriers" className="rounded-lg">Transportadores</TabsTrigger>
          <TabsTrigger value="updates" className="rounded-lg">Atualizações</TabsTrigger>
          <TabsTrigger value="chat" className="rounded-lg">Conversas</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <Card className="premium-card border-0">
            <CardHeader>
              <CardTitle>Visão Geral da API</CardTitle>
              <CardDescription>
                API para integração de sistemas externos com o PR APP
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-slate-50">
                  <h4 className="font-medium text-slate-900 mb-2">Base URL</h4>
                  <code className="text-sm font-mono text-indigo-600">{API_BASE_URL}/api</code>
                </div>
                <div className="p-4 rounded-xl bg-slate-50">
                  <h4 className="font-medium text-slate-900 mb-2">Autenticação</h4>
                  <code className="text-sm font-mono">Header: X-API-Key</code>
                </div>
              </div>

              {/* GET Endpoints */}
              <div className="p-4 rounded-xl bg-green-50 border border-green-200">
                <h4 className="font-medium text-green-900 mb-2">Endpoints GET (Consulta de Dados)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-100 text-green-700 font-mono text-xs">GET</Badge>
                    <code>/external/missions</code>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-100 text-green-700 font-mono text-xs">GET</Badge>
                    <code>/external/missions/stats</code>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-100 text-green-700 font-mono text-xs">GET</Badge>
                    <code>/external/missions/active</code>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-100 text-green-700 font-mono text-xs">GET</Badge>
                    <code>/external/mission/{'{id}'}</code>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-100 text-green-700 font-mono text-xs">GET</Badge>
                    <code>/external/customers</code>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-100 text-green-700 font-mono text-xs">GET</Badge>
                    <code>/external/customers/{'{id}'}</code>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-100 text-green-700 font-mono text-xs">GET</Badge>
                    <code>/external/suppliers</code>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-100 text-green-700 font-mono text-xs">GET</Badge>
                    <code>/external/suppliers/{'{id}'}</code>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-100 text-green-700 font-mono text-xs">GET</Badge>
                    <code>/external/carriers</code>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-100 text-green-700 font-mono text-xs">GET</Badge>
                    <code>/external/carriers/{'{id}'}</code>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-100 text-green-700 font-mono text-xs">GET</Badge>
                    <code>/external/reasons</code>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-100 text-green-700 font-mono text-xs">GET</Badge>
                    <code>/external/tenants</code>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-100 text-green-700 font-mono text-xs">GET</Badge>
                    <code>/external/users</code>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-100 text-green-700 font-mono text-xs">GET</Badge>
                    <code>/external/dashboard/summary</code>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-100 text-green-700 font-mono text-xs">GET</Badge>
                    <code>/external/billing/pending</code>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-100 text-green-700 font-mono text-xs">GET</Badge>
                    <code>/external/search?q=termo</code>
                  </div>
                </div>
              </div>

              {/* POST Endpoints */}
              <div className="p-4 rounded-xl bg-blue-50 border border-blue-200">
                <h4 className="font-medium text-blue-900 mb-2">Endpoints POST (Atualização de Dados)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-blue-100 text-blue-700 font-mono text-xs">POST</Badge>
                    <code>/external/mission/availability</code>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-blue-100 text-blue-700 font-mono text-xs">POST</Badge>
                    <code>/external/mission/arrival</code>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-blue-100 text-blue-700 font-mono text-xs">POST</Badge>
                    <code>/external/mission/report</code>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-blue-100 text-blue-700 font-mono text-xs">POST</Badge>
                    <code>/external/mission/media</code>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-blue-100 text-blue-700 font-mono text-xs">POST</Badge>
                    <code>/external/mission/finalize</code>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-blue-100 text-blue-700 font-mono text-xs">POST</Badge>
                    <code>/external/mission/chat</code>
                  </div>
                </div>
              </div>

              <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                <h4 className="font-medium text-amber-900 mb-2">Exemplo com cURL</h4>
                <CodeBlock>{`curl -X GET "${API_BASE_URL}/api/external/missions?status=em_andamento&days=7" \\
  -H "X-API-Key: prapp_admin_xxxxxxxxxxxxxxxx"`}</CodeBlock>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Availability Tab */}
        <TabsContent value="availability">
          <ScrollArea className="h-[600px]">
            <EndpointCard
              method="POST"
              path="/api/external/mission/availability"
              title="Atualizar Disponibilidade do Fornecedor"
              description="Permite que um fornecedor informe se está disponível para atender uma missão e sua previsão de chegada."
              requestBody={`{
  "mission_id": "abc123-def456-ghi789",
  "supplier_id": "sup001-xyz-123",
  "available": true,
  "eta_datetime": "2026-02-14T15:30:00",
  "notes": "Saindo agora do pátio, trânsito normal"
}`}
              responseBody={`{
  "status": "success",
  "message": "Availability updated",
  "data": {
    "mission_id": "abc123-def456-ghi789",
    "supplier_id": "sup001-xyz-123",
    "supplier_name": "Auto Socorro SP",
    "available": true,
    "eta": "2026-02-14T15:30:00",
    "responded_at": "2026-02-14T14:45:00"
  }
}`}
            >
              <div className="mt-4 p-4 rounded-xl bg-slate-50">
                <h4 className="font-medium text-slate-900 mb-2">Parâmetros</h4>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b">
                      <th className="pb-2">Campo</th>
                      <th className="pb-2">Tipo</th>
                      <th className="pb-2">Obrigatório</th>
                      <th className="pb-2">Descrição</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2 font-mono">mission_id</td>
                      <td>string</td>
                      <td><Badge className="bg-red-100 text-red-700">Sim</Badge></td>
                      <td>ID da missão</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-mono">supplier_id</td>
                      <td>string</td>
                      <td><Badge className="bg-red-100 text-red-700">Sim</Badge></td>
                      <td>ID do fornecedor</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-mono">available</td>
                      <td>boolean</td>
                      <td><Badge className="bg-red-100 text-red-700">Sim</Badge></td>
                      <td>true = disponível, false = indisponível</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-mono">eta_datetime</td>
                      <td>string (ISO)</td>
                      <td><Badge className="bg-slate-100 text-slate-700">Não</Badge></td>
                      <td>Previsão de chegada (formato ISO 8601)</td>
                    </tr>
                    <tr>
                      <td className="py-2 font-mono">notes</td>
                      <td>string</td>
                      <td><Badge className="bg-slate-100 text-slate-700">Não</Badge></td>
                      <td>Observações adicionais</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </EndpointCard>
          </ScrollArea>
        </TabsContent>

        {/* Arrival Tab */}
        <TabsContent value="arrival">
          <ScrollArea className="h-[600px]">
            <EndpointCard
              method="POST"
              path="/api/external/mission/arrival"
              title="Registrar Chegada na Origem"
              description="Registra o momento em que o fornecedor chegou no local da ocorrência. Inicia a contagem de tempo para faturamento."
              requestBody={`{
  "mission_id": "abc123-def456-ghi789",
  "supplier_id": "sup001-xyz-123",
  "arrived_at": "2026-02-14T15:45:00",
  "odometer_start": 145230,
  "notes": "Cheguei no local, motorista aguardando no acostamento"
}`}
              responseBody={`{
  "status": "success",
  "message": "Arrival registered",
  "arrived_at": "2026-02-14T15:45:00",
  "odometer_start": 145230,
  "mission_status": "em_andamento"
}`}
            >
              <div className="mt-4 p-4 rounded-xl bg-slate-50">
                <h4 className="font-medium text-slate-900 mb-2">Parâmetros</h4>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b">
                      <th className="pb-2">Campo</th>
                      <th className="pb-2">Tipo</th>
                      <th className="pb-2">Obrigatório</th>
                      <th className="pb-2">Descrição</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2 font-mono">mission_id</td>
                      <td>string</td>
                      <td><Badge className="bg-red-100 text-red-700">Sim</Badge></td>
                      <td>ID da missão</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-mono">supplier_id</td>
                      <td>string</td>
                      <td><Badge className="bg-red-100 text-red-700">Sim</Badge></td>
                      <td>ID do fornecedor</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-mono">arrived_at</td>
                      <td>string (ISO)</td>
                      <td><Badge className="bg-slate-100 text-slate-700">Não</Badge></td>
                      <td>Data/hora da chegada (usa hora atual se não informado)</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-mono">odometer_start</td>
                      <td>integer</td>
                      <td><Badge className="bg-slate-100 text-slate-700">Não</Badge></td>
                      <td>Hodômetro inicial em km</td>
                    </tr>
                    <tr>
                      <td className="py-2 font-mono">notes</td>
                      <td>string</td>
                      <td><Badge className="bg-slate-100 text-slate-700">Não</Badge></td>
                      <td>Observações sobre a chegada</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </EndpointCard>
          </ScrollArea>
        </TabsContent>

        {/* Report Tab */}
        <TabsContent value="report">
          <ScrollArea className="h-[600px]">
            <EndpointCard
              method="POST"
              path="/api/external/mission/report"
              title="Enviar Breve Relato"
              description="Permite enviar relatos de progresso, problemas ou observações durante o atendimento."
              requestBody={`{
  "mission_id": "abc123-def456-ghi789",
  "supplier_id": "sup001-xyz-123",
  "report_text": "Iniciando troca do pneu dianteiro direito. Pneu estava completamente furado.",
  "report_type": "progress"
}`}
              responseBody={`{
  "status": "success",
  "message": "Report added",
  "report_type": "progress",
  "created_at": "2026-02-14T16:00:00"
}`}
            >
              <div className="mt-4 p-4 rounded-xl bg-slate-50">
                <h4 className="font-medium text-slate-900 mb-2">Tipos de Relato</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-blue-100 text-blue-700">progress</Badge>
                    <span className="text-sm">📋 Progresso do atendimento</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-amber-100 text-amber-700">issue</Badge>
                    <span className="text-sm">⚠️ Problema encontrado</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-slate-100 text-slate-700">observation</Badge>
                    <span className="text-sm">👁️ Observação geral</span>
                  </div>
                </div>
              </div>
            </EndpointCard>
          </ScrollArea>
        </TabsContent>

        {/* Media Tab */}
        <TabsContent value="media">
          <ScrollArea className="h-[600px]">
            <EndpointCard
              method="POST"
              path="/api/external/mission/media"
              title="Anexar Mídia (Fotos, Vídeos, Documentos)"
              description="Permite anexar arquivos de mídia à missão como evidência do atendimento."
              requestBody={`{
  "mission_id": "abc123-def456-ghi789",
  "supplier_id": "sup001-xyz-123",
  "media_url": "https://storage.example.com/missions/photo_odometro_123.jpg",
  "media_type": "photo",
  "description": "Foto do hodômetro no momento da chegada"
}`}
              responseBody={`{
  "status": "success",
  "message": "Media attachment added",
  "evidence_id": "ev001-abc-xyz",
  "media_type": "photo",
  "created_at": "2026-02-14T16:05:00"
}`}
            >
              <div className="mt-4 p-4 rounded-xl bg-slate-50">
                <h4 className="font-medium text-slate-900 mb-2">Tipos de Mídia</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-100 text-green-700">photo</Badge>
                    <span className="text-sm">📷 Fotografias (JPG, PNG, WEBP)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-purple-100 text-purple-700">video</Badge>
                    <span className="text-sm">🎥 Vídeos (MP4, MOV)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-orange-100 text-orange-700">document</Badge>
                    <span className="text-sm">📄 Documentos (PDF)</span>
                  </div>
                </div>
              </div>
              <div className="mt-4 p-4 rounded-xl bg-amber-50 border border-amber-200">
                <h4 className="font-medium text-amber-900 mb-2">⚠️ Importante</h4>
                <p className="text-sm text-amber-800">
                  A URL da mídia deve ser acessível publicamente ou o sistema de storage deve permitir acesso via token.
                  O PR APP armazena apenas a referência (URL) para o arquivo.
                </p>
              </div>
            </EndpointCard>
          </ScrollArea>
        </TabsContent>

        {/* Finalize Tab */}
        <TabsContent value="finalize">
          <ScrollArea className="h-[600px]">
            <EndpointCard
              method="POST"
              path="/api/external/mission/finalize"
              title="Finalizar Missão"
              description="Marca a missão como finalizada, registrando dados finais para cálculo de custos e faturamento."
              requestBody={`{
  "mission_id": "abc123-def456-ghi789",
  "supplier_id": "sup001-xyz-123",
  "finished_at": "2026-02-14T17:30:00",
  "odometer_end": 145280,
  "final_notes": "Atendimento concluído com sucesso. Pneu trocado, veículo liberado para seguir viagem."
}`}
              responseBody={`{
  "status": "success",
  "message": "Mission finalized",
  "finished_at": "2026-02-14T17:30:00",
  "odometer_end": 145280,
  "total_km": 50,
  "total_time_minutes": 105,
  "mission_status": "aguardando_validacao"
}`}
            >
              <div className="mt-4 p-4 rounded-xl bg-green-50 border border-green-200">
                <h4 className="font-medium text-green-900 mb-2">Cálculos Automáticos</h4>
                <p className="text-sm text-green-800">
                  Ao finalizar a missão, o sistema calcula automaticamente:
                </p>
                <ul className="list-disc list-inside text-sm text-green-800 mt-2 space-y-1">
                  <li><strong>Total de KM:</strong> hodômetro_final - hodômetro_inicial</li>
                  <li><strong>Tempo Total:</strong> hora_finalização - hora_chegada (em minutos)</li>
                  <li>O status muda para <code>aguardando_validacao</code> para aprovação</li>
                </ul>
              </div>
            </EndpointCard>
          </ScrollArea>
        </TabsContent>

        {/* Chat Tab */}
        <TabsContent value="chat">
          <ScrollArea className="h-[600px]">
            <EndpointCard
              method="POST"
              path="/api/external/mission/chat"
              title="Enviar Mensagem no Chat"
              description="Adiciona uma mensagem à conversa da missão."
              requestBody={`{
  "mission_id": "abc123-def456-ghi789",
  "sender_type": "supplier",
  "sender_id": "sup001-xyz-123",
  "sender_name": "Auto Socorro SP",
  "content": "Iniciando o atendimento ao veículo."
}`}
              responseBody={`{
  "status": "success",
  "message": "Chat message sent",
  "message_id": "msg001-abc-xyz",
  "created_at": "2026-02-14T16:10:00"
}`}
            />

            <EndpointCard
              method="GET"
              path="/api/external/mission/{mission_id}/chat"
              title="Obter Conversa Completa"
              description="Retorna todas as mensagens da conversa de uma missão em ordem cronológica."
              responseBody={`{
  "mission_id": "abc123-def456-ghi789",
  "mission_code": "PR-20260214-1234",
  "total_messages": 15,
  "messages": [
    {
      "id": "msg001",
      "sender_type": "platform",
      "sender_name": "Sistema",
      "content": "🚨 Nova missão criada",
      "created_at": "2026-02-14T14:00:00"
    },
    {
      "id": "msg002",
      "sender_type": "supplier",
      "sender_name": "Auto Socorro SP",
      "content": "✅ DISPONÍVEL - ETA: 15:30",
      "created_at": "2026-02-14T14:45:00"
    },
    {
      "id": "msg003",
      "sender_type": "api",
      "sender_name": "Auto Socorro SP",
      "content": "🚗 Chegou na origem",
      "created_at": "2026-02-14T15:45:00"
    }
  ]
}`}
            />

            <EndpointCard
              method="GET"
              path="/api/external/mission/{mission_id}"
              title="Obter Dados Completos da Missão"
              description="Retorna todos os dados da missão, incluindo chat, evidências e respostas de fornecedores. Ideal para compilação de relatórios."
              responseBody={`{
  "mission": {
    "id": "abc123-def456-ghi789",
    "code": "PR-20260214-1234",
    "status": "aguardando_validacao",
    "customer_name": "Transportes ABC",
    "supplier_name": "Auto Socorro SP",
    "origin_address": "BR-101 KM 50, São Paulo, SP",
    "vehicle_plate": "ABC-1234",
    "driver_name": "João da Silva",
    "supplier_arrived_at": "2026-02-14T15:45:00",
    "finished_at": "2026-02-14T17:30:00",
    "odometer_start": 145230,
    "odometer_end": 145280,
    "total_km": 50,
    "total_time_minutes": 105
  },
  "chat_history": [...],
  "evidence": [
    {
      "id": "ev001",
      "type": "photo",
      "url": "https://...",
      "description": "Foto do hodômetro"
    }
  ],
  "supplier_responses": [...]
}`}
            />
          </ScrollArea>
        </TabsContent>

        {/* Missions Tab */}
        <TabsContent value="missions">
          <ScrollArea className="h-[600px]">
            <EndpointCard
              method="GET"
              path="/api/external/missions"
              title="Listar Missões"
              description="Retorna lista de missões com filtros opcionais."
              responseBody={`{
  "total": 150,
  "limit": 100,
  "skip": 0,
  "count": 100,
  "missions": [
    {
      "id": "abc123-def456",
      "code": "PR-20260401-1234",
      "status": "em_andamento",
      "customer_name": "LogExpress",
      "supplier_name": "Auto Socorro SP",
      "vehicle_plate": "ABC-1234",
      "created_at": "2026-04-01T10:00:00"
    }
  ]
}`}
            >
              <div className="mt-4 p-4 rounded-xl bg-slate-50">
                <h4 className="font-medium text-slate-900 mb-2">Query Parameters</h4>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b">
                      <th className="pb-2">Parâmetro</th>
                      <th className="pb-2">Tipo</th>
                      <th className="pb-2">Descrição</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2 font-mono">status</td>
                      <td>string</td>
                      <td>Filtrar por status (criada, aguardando_inicio, em_andamento, etc.)</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-mono">customer_id</td>
                      <td>string</td>
                      <td>Filtrar por cliente</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-mono">supplier_id</td>
                      <td>string</td>
                      <td>Filtrar por fornecedor</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-mono">tenant_cnpj</td>
                      <td>string</td>
                      <td>Filtrar por organização</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-mono">days</td>
                      <td>int</td>
                      <td>Número de dias (padrão: 30)</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-mono">limit</td>
                      <td>int</td>
                      <td>Máximo de resultados (padrão: 100, máx: 500)</td>
                    </tr>
                    <tr>
                      <td className="py-2 font-mono">skip</td>
                      <td>int</td>
                      <td>Offset para paginação</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </EndpointCard>

            <EndpointCard
              method="GET"
              path="/api/external/missions/stats"
              title="Estatísticas de Missões"
              description="Retorna estatísticas resumidas das missões."
              responseBody={`{
  "period_days": 30,
  "total_missions": 150,
  "by_status": {
    "criada": 10,
    "aguardando_inicio": 5,
    "em_andamento": 8,
    "finalizada": 100,
    "cancelada": 27
  },
  "completed_missions": 100,
  "average_time_minutes": 45.5,
  "total_km": 5000,
  "average_km": 50.0
}`}
            />

            <EndpointCard
              method="GET"
              path="/api/external/missions/active"
              title="Missões Ativas"
              description="Retorna apenas missões em andamento (criada, aguardando_inicio, em_andamento)."
              responseBody={`{
  "total_active": 23,
  "missions": [...]
}`}
            />

            <EndpointCard
              method="GET"
              path="/api/external/dashboard/summary"
              title="Resumo do Dashboard"
              description="Dados consolidados para exibição em painéis externos."
              responseBody={`{
  "period_days": 30,
  "total_missions": 150,
  "active_missions": 23,
  "status_breakdown": {...},
  "financial": {
    "total_revenue": 125000.00,
    "total_cost": 87500.00,
    "margin": 37500.00
  },
  "performance": {
    "completed_count": 100,
    "avg_completion_time_minutes": 45.5
  }
}`}
            />

            <EndpointCard
              method="GET"
              path="/api/external/billing/pending"
              title="Faturamento Pendente"
              description="Missões aprovadas aguardando faturamento."
              responseBody={`{
  "pending_count": 15,
  "total_value": 25000.00,
  "missions": [...]
}`}
            />

            <EndpointCard
              method="GET"
              path="/api/external/search?q=termo"
              title="Busca Global"
              description="Pesquisa em múltiplas entidades (missões, clientes, fornecedores, transportadores)."
              responseBody={`{
  "query": "ABC",
  "total_results": 12,
  "results": {
    "missions": [...],
    "customers": [...],
    "suppliers": [...],
    "carriers": [...]
  }
}`}
            />
          </ScrollArea>
        </TabsContent>

        {/* Customers Tab */}
        <TabsContent value="customers">
          <ScrollArea className="h-[600px]">
            <EndpointCard
              method="GET"
              path="/api/external/customers"
              title="Listar Clientes"
              description="Retorna lista de clientes cadastrados."
              responseBody={`{
  "total": 50,
  "count": 50,
  "customers": [
    {
      "id": "cust001-abc",
      "name": "LogExpress Transportes",
      "cnpj": "12.345.678/0001-90",
      "phone": "(11) 3456-7890",
      "email": "contato@logexpress.com",
      "city": "São Paulo",
      "state": "SP",
      "active": true,
      "pricing": [...]
    }
  ]
}`}
            >
              <div className="mt-4 p-4 rounded-xl bg-slate-50">
                <h4 className="font-medium text-slate-900 mb-2">Query Parameters</h4>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b">
                      <th className="pb-2">Parâmetro</th>
                      <th className="pb-2">Tipo</th>
                      <th className="pb-2">Descrição</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2 font-mono">active</td>
                      <td>boolean</td>
                      <td>Filtrar por status ativo</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-mono">tenant_cnpj</td>
                      <td>string</td>
                      <td>Filtrar por organização</td>
                    </tr>
                    <tr>
                      <td className="py-2 font-mono">search</td>
                      <td>string</td>
                      <td>Buscar por nome ou CNPJ</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </EndpointCard>

            <EndpointCard
              method="GET"
              path="/api/external/customers/{customer_id}"
              title="Detalhes do Cliente"
              description="Retorna dados completos de um cliente específico."
              responseBody={`{
  "customer": {
    "id": "cust001-abc",
    "name": "LogExpress Transportes",
    "cnpj": "12.345.678/0001-90",
    "phone": "(11) 3456-7890",
    "email": "contato@logexpress.com",
    "address": "Av. Paulista, 1000",
    "city": "São Paulo",
    "state": "SP",
    "active": true,
    "pricing": [
      {
        "service_type": "guincho",
        "base_price": 350.00,
        "km_franchise": 50,
        "km_excess_price": 5.00
      }
    ]
  },
  "stats": {
    "total_missions": 150,
    "active_missions": 3
  }
}`}
            />
          </ScrollArea>
        </TabsContent>

        {/* Suppliers Tab */}
        <TabsContent value="suppliers">
          <ScrollArea className="h-[600px]">
            <EndpointCard
              method="GET"
              path="/api/external/suppliers"
              title="Listar Fornecedores"
              description="Retorna lista de fornecedores cadastrados."
              responseBody={`{
  "total": 30,
  "count": 30,
  "suppliers": [
    {
      "id": "sup001-xyz",
      "name": "Auto Socorro SP",
      "cnpj": "98.765.432/0001-10",
      "phone": "+5511989213046",
      "email": "contato@autosocorro.com",
      "city": "São Paulo",
      "state": "SP",
      "service_types": ["guincho", "mecanico", "pneu"],
      "active": true,
      "pricing": [...]
    }
  ]
}`}
            >
              <div className="mt-4 p-4 rounded-xl bg-slate-50">
                <h4 className="font-medium text-slate-900 mb-2">Query Parameters</h4>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b">
                      <th className="pb-2">Parâmetro</th>
                      <th className="pb-2">Tipo</th>
                      <th className="pb-2">Descrição</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2 font-mono">active</td>
                      <td>boolean</td>
                      <td>Filtrar por status ativo</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-mono">service_type</td>
                      <td>string</td>
                      <td>Filtrar por tipo de serviço</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-mono">city</td>
                      <td>string</td>
                      <td>Filtrar por cidade</td>
                    </tr>
                    <tr>
                      <td className="py-2 font-mono">search</td>
                      <td>string</td>
                      <td>Buscar por nome, telefone ou CNPJ</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </EndpointCard>

            <EndpointCard
              method="GET"
              path="/api/external/suppliers/{supplier_id}"
              title="Detalhes do Fornecedor"
              description="Retorna dados completos de um fornecedor específico."
              responseBody={`{
  "supplier": {
    "id": "sup001-xyz",
    "name": "Auto Socorro SP",
    "phone": "+5511989213046",
    "service_types": ["guincho", "mecanico"],
    "pricing": [...],
    "active": true
  },
  "stats": {
    "total_missions": 250,
    "completed_missions": 230,
    "active_missions": 2
  }
}`}
            />

            <EndpointCard
              method="GET"
              path="/api/external/suppliers/{supplier_id}/missions"
              title="Missões do Fornecedor"
              description="Lista missões atribuídas a um fornecedor específico."
              responseBody={`{
  "supplier_id": "sup001-xyz",
  "count": 25,
  "missions": [...]
}`}
            />
          </ScrollArea>
        </TabsContent>

        {/* Carriers Tab */}
        <TabsContent value="carriers">
          <ScrollArea className="h-[600px]">
            <EndpointCard
              method="GET"
              path="/api/external/carriers"
              title="Listar Transportadores"
              description="Retorna lista de transportadores (empresas de transporte) cadastrados."
              responseBody={`{
  "total": 20,
  "count": 20,
  "carriers": [
    {
      "id": "car001-abc",
      "name": "TransLog Express",
      "cnpj": "11.222.333/0001-44",
      "phone": "(11) 4567-8901",
      "contact_name": "Carlos Silva",
      "fleet_size": 50,
      "active": true
    }
  ]
}`}
            >
              <div className="mt-4 p-4 rounded-xl bg-slate-50">
                <h4 className="font-medium text-slate-900 mb-2">Query Parameters</h4>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b">
                      <th className="pb-2">Parâmetro</th>
                      <th className="pb-2">Tipo</th>
                      <th className="pb-2">Descrição</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2 font-mono">active</td>
                      <td>boolean</td>
                      <td>Filtrar por status ativo</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-mono">tenant_cnpj</td>
                      <td>string</td>
                      <td>Filtrar por organização</td>
                    </tr>
                    <tr>
                      <td className="py-2 font-mono">search</td>
                      <td>string</td>
                      <td>Buscar por nome ou CNPJ</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </EndpointCard>

            <EndpointCard
              method="GET"
              path="/api/external/carriers/{carrier_id}"
              title="Detalhes do Transportador"
              description="Retorna dados completos de um transportador específico."
              responseBody={`{
  "carrier": {
    "id": "car001-abc",
    "name": "TransLog Express",
    "cnpj": "11.222.333/0001-44",
    "phone": "(11) 4567-8901",
    "contact_name": "Carlos Silva",
    "fleet_size": 50,
    "active": true
  },
  "stats": {
    "total_missions": 500
  }
}`}
            />

            <EndpointCard
              method="GET"
              path="/api/external/reasons"
              title="Listar Motivos de Missão"
              description="Retorna lista de motivos de missão cadastrados."
              responseBody={`{
  "total": 9,
  "reasons": [
    {
      "id": "rea001",
      "code": "PM",
      "name": "Pane Mecânica",
      "description": "Pane Mecânica",
      "category": "mecanica",
      "active": true
    }
  ]
}`}
            />

            <EndpointCard
              method="GET"
              path="/api/external/tenants"
              title="Listar Organizações"
              description="Retorna lista de organizações/tenants (apenas admin)."
              responseBody={`{
  "total": 2,
  "tenants": [
    {
      "id": "ten001",
      "cnpj": "12.345.678/0001-90",
      "company_name": "Concectiva Tecnologia",
      "trading_name": "PR APP",
      "approval_status": "approved",
      "approved": true
    }
  ]
}`}
            />

            <EndpointCard
              method="GET"
              path="/api/external/users"
              title="Listar Usuários"
              description="Retorna lista de usuários (sem dados sensíveis)."
              responseBody={`{
  "total": 10,
  "users": [
    {
      "id": "usr001",
      "email": "admin@prapp.com.br",
      "name": "Administrador Master",
      "role": "mestre",
      "tenant_cnpj": null,
      "active": true
    }
  ]
}`}
            />
          </ScrollArea>
        </TabsContent>

        {/* Updates Tab (POST endpoints) */}
        <TabsContent value="updates">
          <ScrollArea className="h-[600px]">
            <EndpointCard
              method="POST"
              path="/api/external/mission/availability"
              title="Atualizar Disponibilidade"
              description="Atualiza a disponibilidade de um fornecedor para uma missão."
              requestBody={`{
  "mission_id": "abc123-def456",
  "supplier_id": "sup001-xyz",
  "available": true,
  "eta_datetime": "2026-04-01T15:30:00",
  "notes": "A caminho"
}`}
              responseBody={`{
  "status": "success",
  "message": "Availability updated"
}`}
            />

            <EndpointCard
              method="POST"
              path="/api/external/mission/arrival"
              title="Registrar Chegada"
              description="Registra a chegada do fornecedor na origem."
              requestBody={`{
  "mission_id": "abc123-def456",
  "supplier_id": "sup001-xyz",
  "arrived_at": "2026-04-01T15:45:00",
  "odometer_start": 145230,
  "notes": "Cheguei no local"
}`}
              responseBody={`{
  "status": "success",
  "arrived_at": "2026-04-01T15:45:00",
  "mission_status": "em_andamento"
}`}
            />

            <EndpointCard
              method="POST"
              path="/api/external/mission/report"
              title="Enviar Relato"
              description="Adiciona um relato de progresso à missão."
              requestBody={`{
  "mission_id": "abc123-def456",
  "supplier_id": "sup001-xyz",
  "report_text": "Iniciando troca do pneu",
  "report_type": "progress"
}`}
              responseBody={`{
  "status": "success",
  "report_type": "progress"
}`}
            />

            <EndpointCard
              method="POST"
              path="/api/external/mission/media"
              title="Anexar Mídia"
              description="Anexa foto, vídeo ou documento à missão."
              requestBody={`{
  "mission_id": "abc123-def456",
  "supplier_id": "sup001-xyz",
  "media_url": "https://storage.example.com/photo.jpg",
  "media_type": "photo",
  "description": "Foto do hodômetro"
}`}
              responseBody={`{
  "status": "success",
  "evidence_id": "ev001-abc"
}`}
            />

            <EndpointCard
              method="POST"
              path="/api/external/mission/finalize"
              title="Finalizar Missão"
              description="Marca a missão como finalizada."
              requestBody={`{
  "mission_id": "abc123-def456",
  "supplier_id": "sup001-xyz",
  "finished_at": "2026-04-01T17:30:00",
  "odometer_end": 145280,
  "final_notes": "Atendimento concluído"
}`}
              responseBody={`{
  "status": "success",
  "total_km": 50,
  "total_time_minutes": 105,
  "mission_status": "aguardando_validacao"
}`}
            />
          </ScrollArea>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ApiDocumentationPage;
